/**
 * Validation logic for AI-managed object configurations.
 *
 * Validation is intentionally centralised here so that every consumer
 * (editor plugin UI, server-side checks, etc.) uses the same rules.
 * The function is designed to be extended as new config fields are
 * introduced — callers should not hard-code their own checks.
 */

import type { AiManagedObjectSourceEntry } from '../sources/types.ts';
import type { AiManagedObjectConfigCreateInput } from './types.ts';

export type ValidationErrorCode =
	| 'EMPTY_PROMPT'
	| 'INVALID_DESIRED_UPDATE_FREQUENCY'
	| 'MISSING_GOAL'
	| 'MISSING_OUTPUT_FORMAT'
	| 'MISSING_SOURCES'
	| 'SOURCES_INVALID'
	| 'SOURCES_PENDING'
	| 'SOURCES_REQUIRED';

export type SourceValidationError =
	| 'SOURCES_INVALID'
	| 'SOURCES_PENDING'
	| 'SOURCES_REQUIRED'
	| null;

/** Describes a single validation failure. */
export interface ValidationError {
	/** Stable code that UI consumers can map to localized messages. */
	code: ValidationErrorCode;
	/** The config field that failed validation. */
	field: string;
}

/** Result of validating an AI-managed-object configuration. */
export interface ValidationResult {
	/** Non-empty only when `valid` is `false`. */
	errors: ValidationError[];
	/** `true` when the config passes all checks. */
	valid: boolean;
}

/**
 * Validate that an AI-managed-object configuration is ready to be persisted.
 *
 * Currently validates:
 * - manual prompt config must have a non-empty prompt.
 * - structured config must have a goal, output format, and at least one source.
 */
export function validateAiManagedObjectConfig(
	config: AiManagedObjectConfigCreateInput,
): ValidationResult {
	const errors: ValidationError[] = [];
	const hasStructuredConfig = Boolean(config.goal);

	if (!hasStructuredConfig && (!config.prompt || config.prompt.trim().length === 0)) {
		errors.push({
			code: 'EMPTY_PROMPT',
			field: 'prompt',
		});
	}

	if (
		config.desiredUpdateFrequency &&
		(!config.desiredUpdateFrequency.type || config.desiredUpdateFrequency.value.trim().length === 0)
	) {
		errors.push({
			code: 'INVALID_DESIRED_UPDATE_FREQUENCY',
			field: 'desiredUpdateFrequency',
		});
	}

	if (hasStructuredConfig) {
		if (!config.goal) {
			errors.push({
				code: 'MISSING_GOAL',
				field: 'goal',
			});
		}

		if (!config.goalOperations?.outputFormat) {
			errors.push({
				code: 'MISSING_OUTPUT_FORMAT',
				field: 'goalOperations.outputFormat',
			});
		}

		if (!config.sources?.length) {
			errors.push({
				code: 'MISSING_SOURCES',
				field: 'sources',
			});
		}
	}

	return {
		valid: errors.length === 0,
		errors,
	};
}

export function getSourceValidationError({
	requireSource,
	sourceEntries,
}: {
	requireSource: boolean;
	sourceEntries: AiManagedObjectSourceEntry[];
}): SourceValidationError {
	if (sourceEntries.length === 0) {
		return requireSource ? 'SOURCES_REQUIRED' : null;
	}

	if (sourceEntries.some((source) => source.status === 'resolving')) {
		return 'SOURCES_PENDING';
	}

	if (sourceEntries.some((source) => source.status === 'invalid')) {
		return 'SOURCES_INVALID';
	}

	if (requireSource && !sourceEntries.some((source) => source.status === 'resolved')) {
		return 'SOURCES_REQUIRED';
	}

	return null;
}
